import React, { useState, useEffect } from 'react';
import { API_BASE_URL as API_BASE } from '../../config/apiConfig';
import { ShoppingCart, ToggleLeft, ToggleRight, Loader2, CheckCircle2, AlertTriangle, Mail } from 'lucide-react';

export default function SubscriptionSettings() {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null); // key being saved
    const [toast, setToast] = useState(null);   // { type: 'success'|'error', msg }

    const token = localStorage.getItem('token');

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/admin/settings`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setSettings(Array.isArray(data) ? data : []);
            } else {
                setSettings([]);
            }
        } catch (e) {
            console.error(e);
            setSettings([]);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchSettings(); }, []);

    const toggle = async (key, currentValue) => {
        const newValue = currentValue === 'true' ? 'false' : 'true';
        setSaving(key);
        try {
            const res = await fetch(`${API_BASE}/admin/settings/${key}`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ value: newValue }),
            });
            if (!res.ok) throw new Error(await res.text());
            setSettings(prev => prev.map(s => s.key === key ? { ...s, value: newValue } : s));
            setToast({ type: 'success', msg: `'${key}' set to ${newValue}` });
        } catch (e) {
            setToast({ type: 'error', msg: String(e) });
        } finally {
            setSaving(null);
            setTimeout(() => setToast(null), 4000);
        }
    };

    const LABEL_MAP = {
        subscriptions_enabled: {
            title: 'Self-Serve Subscriptions',
            icon: ShoppingCart,
            on: 'Standard & Pro plans purchasable online',
            off: 'Contact Sales mode active — purchase buttons replaced',
        },
    };

    return (
        <div className="p-8 max-w-3xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-200/50">
                    <ShoppingCart className="w-6 h-6 text-white" />
                </div>
                <div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">Subscription Settings</h2>
                    <p className="text-sm text-slate-500 font-medium">Toggle self-serve plan purchasing on or off without a deployment</p>
                </div>
            </div>

            {/* Toast */}
            {toast && (
                <div className={`flex items-center gap-3 px-5 py-3.5 rounded-2xl mb-6 text-sm font-semibold shadow-md ${
                    toast.type === 'success'
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                        : 'bg-rose-50 border border-rose-200 text-rose-700'
                }`}>
                    {toast.type === 'success'
                        ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                        : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
                    {toast.msg}
                </div>
            )}

            {loading ? (
                <div className="flex items-center gap-3 text-slate-500 py-16 justify-center">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-medium">Loading settings…</span>
                </div>
            ) : (
                <div className="space-y-4">
                    {(Array.isArray(settings) ? settings : []).map(setting => {
                        const meta = LABEL_MAP[setting.key];
                        if (!meta) return null;
                        const Icon = meta.icon;
                        const isOn = setting.value === 'true';
                        const isSaving = saving === setting.key;

                        return (
                            <div
                                key={setting.key}
                                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-start justify-between gap-6 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start gap-4">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isOn ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                                        <Icon className={`w-5 h-5 ${isOn ? 'text-emerald-600' : 'text-amber-500'}`} />
                                    </div>
                                    <div>
                                        <p className="font-black text-slate-900 text-base">{meta.title}</p>
                                        <p className="text-sm text-slate-500 mt-0.5 font-medium">{setting.description}</p>
                                        <div className={`inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wide border ${
                                            isOn
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                : 'bg-amber-50 text-amber-700 border-amber-200'
                                        }`}>
                                            {isOn ? '✓ Enabled' : '⚠ Contact Sales Mode'}
                                        </div>
                                        <p className="text-xs text-slate-400 mt-1.5 font-medium italic">
                                            {isOn ? meta.on : meta.off}
                                        </p>
                                        {!isOn && (
                                            <div className="flex items-center gap-1.5 mt-2 text-xs text-violet-600 font-semibold">
                                                <Mail className="w-3 h-3" />
                                                Inquiries go to gary@developmentanalyzer.com
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Toggle */}
                                <button
                                    onClick={() => toggle(setting.key, setting.value)}
                                    disabled={isSaving}
                                    className={`flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
                                        isSaving
                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                            : isOn
                                                ? 'bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100'
                                                : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-200'
                                    }`}
                                >
                                    {isSaving ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : isOn ? (
                                        <ToggleRight className="w-4 h-4" />
                                    ) : (
                                        <ToggleLeft className="w-4 h-4" />
                                    )}
                                    {isSaving ? 'Saving…' : isOn ? 'Disable (Contact Sales)' : 'Enable Online Sign-Up'}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Info box */}
            <div className="mt-8 bg-indigo-50/50 border border-indigo-100 rounded-2xl p-5 text-sm text-indigo-700 font-medium">
                <p className="font-black mb-1 text-indigo-900">How it works</p>
                <ul className="list-disc list-inside space-y-1 text-indigo-600">
                    <li><strong>Disabled (default):</strong> Pricing page shows "Contact Sales" buttons. Clicking opens a pre-filled email to gary@developmentanalyzer.com.</li>
                    <li><strong>Enabled:</strong> Pricing page shows normal Stripe checkout flow. Users can self-serve Standard or Pro.</li>
                    <li>Changes take effect <strong>immediately</strong> for all users — no restart or deployment needed.</li>
                </ul>
            </div>
        </div>
    );
}
